import React, { useState, useEffect } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useUserGroups } from '../hooks/useUserGroups';
import NotAuthorized from './NotAuthorized';
import TenantNotAllowed from './TenantNotAllowed';
import { msalInstance } from '../auth/msalInstance';
import { loginRequest, isTenantAllowed, getTenantIdFromAccount, performLocalLogout } from '../auth/msalConfig';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredGroups?: string[]; // Array of allowed group names
}

const SIGNED_OUT_KEY = 'reporting-central-signed-out';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredGroups }) => {

  // Always call hooks at the top of the component
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const [signedOut, setSignedOut] = useState<boolean>(() => {
    return localStorage.getItem(SIGNED_OUT_KEY) === 'true';
  });
  const [tenantAllowed, setTenantAllowed] = useState<boolean | null>(null);
  const { groups, loading: groupsLoading } = useUserGroups();

  // Validate tenant when account is available
  useEffect(() => {
    if (accounts.length > 0 && isAuthenticated) {
      const account = accounts[0];
      const tenantId = getTenantIdFromAccount(account);
      
      if (tenantId) {
        const allowed = isTenantAllowed(tenantId);
        setTenantAllowed(allowed);
        
        // If tenant is not allowed, sign out the user locally
        if (!allowed) {
          console.warn(`Tenant ${tenantId} is not in the allowed tenants list`);
          performLocalLogout(instance);
        }
      } else {
        // If we can't determine tenant, deny access (fail-safe)
        console.warn('Could not determine tenant ID from account');
        setTenantAllowed(false);
      }
    } else {
      setTenantAllowed(null);
    }
  }, [accounts, isAuthenticated, instance]);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.removeItem(SIGNED_OUT_KEY);
      setSignedOut(false);
    } else if (accounts.length > 0) {
      // Try to acquire token silently if we have accounts but aren't authenticated
      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      }).catch((error) => {
        console.error('Silent token acquisition failed:', error);
        // Clear the account if silent acquisition fails
        instance.clearCache();
        setSignedOut(true);
      });
    }
  }, [isAuthenticated, accounts, instance]);

  // Show loading while MSAL or group info is initializing, or while validating tenant
  if (inProgress !== InteractionStatus.None || groupsLoading || (isAuthenticated && tenantAllowed === null)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if tenant is allowed (only if authenticated)
  if (isAuthenticated && tenantAllowed === false) {
    return <TenantNotAllowed />;
  }

  // If signed out, show welcome screen (no auto-login)
  if (signedOut || !isAuthenticated) {
    return (
      <div className="login-page-container">
        <div className="login-page-card">
          <div className="login-page-content">
            <img
              src="/hilco-vision-full-logo.png"
              alt="Hilco Vision Logo"
              className="login-page-logo"
            />
            <h2 className="login-page-title">
              Welcome to Reports Central
            </h2>
            <p className="login-page-desc">
              Please sign in with your company account to access reports.
            </p>
            <button
              onClick={() => {
                localStorage.removeItem(SIGNED_OUT_KEY);
                setSignedOut(false);
                instance.loginRedirect();
              }}
              className="login-page-signin-btn"
              onMouseOver={e => (e.currentTarget.style.background = '#007bb8')}
              onMouseOut={e => (e.currentTarget.style.background = '#0091d0')}
            >
              Sign in with your Company Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If requiredGroups is set, check group membership (case-insensitive comparison)
  if (requiredGroups && requiredGroups.length > 0) {
    const normalize = (s: string) => s.trim().toLowerCase();
    const normalizedUserGroups = groups.map(normalize);
    const normalizedRequiredGroups = requiredGroups.map(normalize);
    const isInGroup = normalizedUserGroups.some(userGroup => 
      normalizedRequiredGroups.includes(userGroup)
    );
    if (!isInGroup) {
      return <NotAuthorized />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute; 